import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import React from 'react';
import {IconType} from '@Components/icon-v2/icon.types';
import {Type} from '@Components/button';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from '../../../../../panels/share-panel/share-panel.module.scss';
import {ShareDesign} from '@Panels/share-panel/components/share-design/share-design';
import type {NavShareOptionProps} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-share-option/nav-share-option.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import LoadingPanel from '@Panels/loading-panel/loading-panel';
import {updateIsPosterShared} from '@Components/poster-editor/poster-editor-reducer';
import {addClickOutsideListener} from '@Utils/dom.util';

export function NavShareOption({...props}: NavShareOptionProps): ReactElement<any> {
  const [isOpened, setIsOpened] = useState(false);
  const [hashedId, setHashedId] = useState('');
  const dispatch = useAppDispatch();

  const isPosterSaving = useAppSelector((state) => {
    return state.posterEditor.isPosterSaving;
  });

  const isPosterShared = useAppSelector((state) => {
    return state.posterEditor.isPosterShared;
  });

  useEffect(() => {
    const cleanup = addClickOutsideListener('.js-nav-share-option-dropdown', () => {
      setIsOpened(false);
    });

    return (): void => {
      cleanup();
    };
  }, [isOpened]);

  const onClickShare = (): void => {
    if (isOpened) {
      return;
    }

    void window.posterEditor?.whiteboard?.savePoster.save().then(() => {
      setHashedId(window.posterEditor?.whiteboard?.hashedID ?? '');
    });
  };

  const getIcon = (): string => {
    if (isPosterShared) {
      return 'icon-group_add';
    }

    return 'icon-lock';
  };

  const onShareDesign = (isShared: boolean): void => {
    dispatch(updateIsPosterShared(isShared));
  };

  const getSelector = (): ReactElement<any> => {
    return (
      <div>
        <BaseNavOption
          id="nav-collaborate-option"
          icon={getIcon()}
          text={window.i18next.t('pmwjs_share')}
          optionType={props.collaborateOptionType}
          onClickHandler={onClickShare}
          hasMarginRight
          iconType={IconType.DARK_SECONDARY}
          buttonType={Type.DARK_SECONDARY}
          isLoading={props.isLoading}
          isActive={isOpened}
        />
      </div>
    );
  };

  const getPopup = (): ReactElement<any> => {
    return (
      <div className={`${styles.panel} ${styles.noOverflow} _full-width _full-height flex-v-row`}>
        {!isPosterSaving ? <ShareDesign itemHashedId={hashedId} onShare={onShareDesign} /> : <LoadingPanel />}
      </div>
    );
  };

  return (
    <Dropdown
      popUpHasCustomHeight
      popUpHasCustomWidth
      popupClassName={`${styles.panel} ${styles.panelPopup} flex-v-row spacing-p-4 flex-align-items-center ${isPosterSaving ? 'flex-content-center' : ''}`}
      selector={getSelector()}
      popup={getPopup()}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      position={DROPDOWN_POSITION.FIXED}
      doCloseOnInnerClick={false}
      doesCloseOnOuterClick={false}
      open={isOpened}
      onOpen={() => {
        setIsOpened(true);
      }}
      onClose={() => {
        setIsOpened(false);
      }}
      className="js-nav-share-option-dropdown"
    />
  );
}
